import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { cn } from "../lib/cn";

const control =
  "min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 text-base text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-muted)] disabled:opacity-60 sm:text-sm";

/**
 * The control is 44px tall and 16px text on a phone -- below 16px iOS Safari
 * zooms the whole page on focus, which is the classic mobile form bug. It drops
 * to 14px from `sm` where there is no such behaviour.
 */
function Wrapper({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-[var(--text-primary)]">
        {label}
        {required && <span className="ml-0.5 text-danger-600">*</span>}
      </span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-danger-600">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-[var(--text-muted)]">{hint}</span>
      ) : null}
    </label>
  );
}

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextField({ label, hint, error, className, required, ...rest }: TextFieldProps) {
  return (
    <Wrapper label={label} hint={hint} error={error} required={required}>
      <input
        aria-invalid={error ? true : undefined}
        className={cn(control, error && "border-danger-500", className)}
        required={required}
        {...rest}
      />
    </Wrapper>
  );
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
}

export function SelectField({
  label,
  hint,
  error,
  options,
  className,
  required,
  ...rest
}: SelectFieldProps) {
  return (
    <Wrapper label={label} hint={hint} error={error} required={required}>
      <select
        aria-invalid={error ? true : undefined}
        className={cn(control, "pr-8", error && "border-danger-500", className)}
        required={required}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Wrapper>
  );
}

export interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function TextAreaField({
  label,
  hint,
  error,
  className,
  required,
  ...rest
}: TextAreaFieldProps) {
  return (
    <Wrapper label={label} hint={hint} error={error} required={required}>
      <textarea
        aria-invalid={error ? true : undefined}
        className={cn(control, "min-h-24 resize-y py-2", error && "border-danger-500", className)}
        required={required}
        {...rest}
      />
    </Wrapper>
  );
}
